/**
 * Assessment calendar days and date-only UI use UTC to match backend
 * (`utc_now` / `utc_now_date`, due-date locks, publish stamps).
 */
import { formatInTimeZone } from "date-fns-tz";

const UTC = "UTC";
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

let serverToday: string | null = null;

/** Backend `utc_now_date` from `X-Server-Today` (Test as-of when Test Mode is on). */
export const setServerToday = (value?: string | null): void => {
  if (!value) return;
  const trimmed = value.trim();
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    serverToday = trimmed;
  }
};

export const serverNow = (): Date => {
  if (serverToday) {
    return new Date(`${serverToday}T00:00:00.000Z`);
  }
  return new Date();
};
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Parse API ISO or YYYY-MM-DD as a UTC instant (date-only → midnight UTC). */
export const parseAssessmentDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;

  if (DATE_ONLY_PATTERN.test(raw)) {
    const date = new Date(`${raw}T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const normalized = raw.replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const candidate = hasZone ? normalized : `${normalized}Z`;
  const date = new Date(candidate);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfUtcDayMs = (d: Date): number =>
  Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

/** YYYY-MM-DD in UTC for filters and `<input type="date">`. */
export const toUtcDateInputValue = (isoDate?: string | null): string => {
  if (!isoDate) return "";
  const trimmed = isoDate.trim();
  if (!trimmed) return "";

  if (DATE_ONLY_PATTERN.test(trimmed)) return trimmed;

  const date = parseAssessmentDate(trimmed);
  if (!date) return "";
  return formatInTimeZone(date, UTC, "yyyy-MM-dd");
};

export const utcTodayInputValue = (): string =>
  formatInTimeZone(serverNow(), UTC, "yyyy-MM-dd");

/** Today (UTC) + N calendar days as YYYY-MM-DD. */
export const addUtcDaysToToday = (days: number): string => {
  const now = serverNow();
  const utc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days)
  );
  return formatInTimeZone(utc, UTC, "yyyy-MM-dd");
};

/** Today (UTC) minus N calendar months as YYYY-MM-DD. */
export const utcMonthsAgoInputValue = (months: number): string => {
  const now = serverNow();
  const utc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, now.getUTCDate())
  );
  return formatInTimeZone(utc, UTC, "yyyy-MM-dd");
};

export const isUtcDateBeforeToday = (value?: string | null): boolean => {
  const d = parseAssessmentDate(value);
  if (!d) return false;
  return startOfUtcDayMs(d) < startOfUtcDayMs(serverNow());
};

export const isUtcDateOverdue = (value?: string | null): boolean => {
  const d = parseAssessmentDate(value);
  if (!d) return false;
  return startOfUtcDayMs(d) < startOfUtcDayMs(serverNow());
};

export type AssessmentDateFormat = "DMY" | "DMY_SHORT" | "US_SHORT" | "US_LONG";

/** Date-only label in UTC (no local browser shift). */
export const formatAssessmentDateUtc = (
  value?: string | null,
  format: AssessmentDateFormat = "DMY_SHORT"
): string => {
  const date = parseAssessmentDate(value);
  if (!date) return value ? String(value) : "";

  const day = date.getUTCDate();
  const monthIdx = date.getUTCMonth();
  const year = date.getUTCFullYear();
  const dayPad = String(day).padStart(2, "0");
  const monthPad = String(monthIdx + 1).padStart(2, "0");
  const shortMonth = MONTH_SHORT[monthIdx];

  switch (format) {
    case "DMY":
      return `${dayPad}/${monthPad}/${year}`;
    case "US_SHORT":
      return `${monthPad}/${dayPad}/${year}`;
    case "US_LONG":
      return `${shortMonth} ${day}, ${year}`;
    case "DMY_SHORT":
    default:
      return `${dayPad} ${shortMonth} ${year}`;
  }
};

/** Date + time label in UTC (12h). */
export const formatAssessmentDateTimeUtc = (
  value?: string | null,
  dateOnly = false
): string => {
  const date = parseAssessmentDate(value);
  if (!date) return value ? String(value) : "";

  const datePart = formatAssessmentDateUtc(value, "US_SHORT");
  if (dateOnly || !datePart) return datePart || "-";

  const hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = String(hours % 12 || 12).padStart(2, "0");
  return `${datePart} ${displayHours}:${minutes} ${ampm}`;
};

/** Same shape as `formatDateTime` but UTC calendar/clock. */
export const formatAssessmentDateTimePartsUtc = (
  value?: string | null,
  format: "DMY" | "DMY_SHORT" = "DMY"
): { date: string; time: string; dateTime: string } => {
  if (!value) return { date: "-", time: "-", dateTime: "-" };
  const date = parseAssessmentDate(value);
  if (!date) return { date: "-", time: "-", dateTime: "-" };

  const dateLabel = formatAssessmentDateUtc(
    value,
    format === "DMY" ? "DMY" : "DMY_SHORT"
  );
  const hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";
  const timeLabel = `${hours % 12 || 12}:${minutes} ${period}`;

  return {
    date: dateLabel || "-",
    time: timeLabel,
    dateTime: `${dateLabel} ${timeLabel}`,
  };
};

/** e.g. "20th Nov" using UTC day/month. */
export const formatAssessmentDateOrdinalUtc = (
  value?: string | null
): string => {
  const date = parseAssessmentDate(value);
  if (!date) return value ? String(value) : "";

  const day = date.getUTCDate();
  const month = MONTH_SHORT[date.getUTCMonth()];
  const getOrdinalSuffix = (n: number) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return `${day}${getOrdinalSuffix(day)} ${month}`;
};

/**
 * Relative phrase; absolute-date fallback uses UTC day so it matches
 * assessment calendar stamps.
 */
export const formatAssessmentRelativeTimeAgoUtc = (
  dateString?: string | null
): string | null => {
  if (!dateString) return null;
  const date = parseAssessmentDate(dateString);
  if (!date) return null;

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) {
    const formatted = formatAssessmentDateUtc(dateString, "DMY_SHORT");
    return formatted ? `on ${formatted}` : null;
  }

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;

  const formatted = formatAssessmentDateUtc(dateString, "DMY_SHORT");
  return formatted ? `on ${formatted}` : null;
};
